import { Selector, SelectorProps } from "@/components/selector/selector"
import { createContext, useCallback, useContext, useState } from "react"

// re-export all the items from the selector, so components can import
// from here instead of the actual component file. Later, the goal is merge
// the component into this provider.
export * from "@/components/selector/selector"

type SelectorContextProps = {
  showSelector: (config: SelectorProps | null) => void
  isSelectorShown: boolean
}

/*
 * The provider exports a <SelectorProvider /> that wraps over children components that intend
 * to use the custom <Selector /> component for choices.
 *
 * The exported useSelector() custom hook function returns reference to a function to show/hide
 * the selector, and a boolean on whether the selector is currently being shown.
 *
 * TODO - move the <Selector /> component also inside this file so they are both togther.
 */

// setup a context to provide the showSelector() method to all child components
// the showSelector() method returned by this hook takes an object of type SelectorProps
// call showSelector(null) to hide the selector (if its being shown)
const ToggleSelectorContext = createContext<SelectorContextProps>({
  isSelectorShown: false,
  showSelector: () => {},
})

export function SelectorProvider({ children }) {
  // to show the selector, invoke setSelector() with a non-null object containing the options/callback
  // the setSelector() is provided to all children components via the ToggleSelectorContext below
  const [selectorConfig, setSelectorConfig] = useState<SelectorProps | null>(
    null
  )
  const [isVisible, setIsVisible] = useState(false)

  const showSelector = useCallback((config: SelectorProps | null) => {
    if (config) {
      setSelectorConfig(config)
      setTimeout(() => setIsVisible(true), 0)
    } else {
      setIsVisible(false)
      setTimeout(() => setSelectorConfig(null), 500)
    }
  }, [])

  // wrap the children over the Context to pass them the setSelector() method
  return (
    <ToggleSelectorContext.Provider
      value={{ showSelector, isSelectorShown: isVisible }}>
      {children}

      {/*
       * <Selector /> renders an accordian (or a list) to make selections. The 'selector' object is null by default
       * which "hides" it. To show the accordian, a child component invokes showSelector() with an object
       * containing the possible options, the currently selectedKey and the onSelect callback after selection (see SelectorProps)
       */}
      <div
        className={`absolute inset-0 z-20 ${selectorConfig ? "block" : "hidden"}`}>
        <div
          className={`fixed ${
            isVisible ? "opacity-100" : "opacity-0"
          } inset-0 bg-main-700/50 transition-opacity duration-700`}
        />
        <div
          className={`relative w-full transform transition-transform duration-700 ease-in-out ${
            isVisible ? "" : "-translate-y-[90vh]"
          }`}>
          {selectorConfig && <Selector config={selectorConfig} />}
        </div>
      </div>
    </ToggleSelectorContext.Provider>
  )
}

// export hook function for children to access the showSelector() method
export function useSelector() {
  const { isSelectorShown, showSelector } = useContext(ToggleSelectorContext)
  return { showSelector, isSelectorShown }
}
